import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, Input, AfterViewInit, OnDestroy } from '@angular/core';

import { Observable, of, Subject } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';

import { TranslateService } from '@ngx-translate/core';

import { AdminService } from 'modules/core/services/admin.service';

import { DonutChartConfig, DonutChartOptions } from 'uber-ui';

import { ITasksStats, Task } from 'modules/shared/models/task.models';

import { ThemeService } from 'modules/shared/services/theme.service';

interface DonutChartColorPalette {
  [themeName: string]: DonutChartOptions;
}

@Component({
  selector: 'artemis-tasks-card',
  templateUrl: './tasks-card.component.html',
  styleUrls: ['./tasks-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TasksCardComponent implements OnInit, AfterViewInit, OnDestroy {

  private unsubscribe$: Subject<void> = new Subject();

  filteredTask: Array<Task>;
  currentFilter: string = '';
  currentTable: string = '';
  overdueTaskPeriod$: Observable<object>;

  @Input() pendingTasks: Array<Task> = [];
  @Input() overdueTasks: Array<Task>;

  @Input() set tasksStats(stats: ITasksStats) {
    this.donutData = <Array<DonutChartConfig>>[];
    if (stats) {
      this.donutData = [
        {
          name: `${this.translateService.instant('dashboard.chart.title.pending_case')}`,
          color1: '#0abc9e',
          color2: '#65e0cb',
          value: stats.pendingTaskCount
        }, {
          name: `${this.translateService.instant('dashboard.chart.title.overdue_case')}`,
          color1: '#e6365d',
          color2: '#e65876',
          value: stats.overdueTaskCount
        }, {
          name: `${this.translateService.instant('dashboard.chart.title.periodic_review')}`,
          color1: '#1c8bb4',
          color2: '#52c3cb',
          value: stats.ongoingDueDiligenceCount
        }, {
          name: `${this.translateService.instant('dashboard.chart.title.expiring_document')}`,
          color1: '#ffb744',
          color2: '#ffdfaa',
          value: stats.expiringDocCount
        }
      ];
    }
    this.cdRef.detectChanges();
  }

  donutData: Array<DonutChartConfig>;
  tasksChartTitle: string = `${this.translateService.instant('dashboard.chart.title.task')}`;

  donutChartColorPalette: DonutChartColorPalette = <DonutChartColorPalette>{
    theme_dark: {
      chartFontColor: '#fff',
      legendFontColor: '#fff',
      legendBackgroundColor: '#0b1f2a',
      dividerColor: '#286170'
    },
    theme_light: {
      chartFontColor: '#000',
      legendFontColor: '#000',
      legendBackgroundColor: '#e7e7e7',
      dividerColor: '#bdbdbd'
    },
  };

  currentTheme: string = 'theme_dark';

  constructor(protected translateService: TranslateService,
              private cdRef: ChangeDetectorRef,
              private themeService: ThemeService,
              private adminService: AdminService) { }

  ngOnInit() {
    this.overdueTaskPeriod$ = this.adminService
      .getConfig('OVERDUE_TASK_PERIOD')
      .pipe(
        takeUntil(this.unsubscribe$),
        map((overdueCase: object) => overdueCase),
        catchError(() => of({key: 'OVERDUE_TASK_PERIOD', value: 14}))
      );
  }

  ngAfterViewInit() {
    this.checkCurrentTheme();
  }

  ngOnDestroy() {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  /**
   * @method checkCurrentTheme
   * Check current theme (light or dark)
   */
  private checkCurrentTheme(): void {
    this.themeService
      .getTheme()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (currentTheme: string) => {
          if (currentTheme) {
            this.currentTheme = currentTheme;
            this.cdRef.detectChanges();
          }
        }
      );
  }

  /**
   * Filter table or open corresponding page when user click on chart
   * @method onSelectHandler
   */
  onSelectHandler(event: DonutChartConfig): void {
    if (event) {
      this.currentFilter = event.name;
      switch (event.name) {
        case `${this.translateService.instant('dashboard.chart.title.pending_case')}`:
          this.currentTable = 'Pending Task Table';
          break;

        case `${this.translateService.instant('dashboard.chart.title.overdue_case')}`:
          this.currentTable = 'Overdue Task Table';
          break;

        case `${this.translateService.instant('dashboard.chart.title.expiring_document')}`:
          this.currentTable = 'Expiring Document Table';
          break;

        case `${this.translateService.instant('dashboard.chart.title.periodic_review')}`:
          this.currentTable = 'Periodic Review Table';
          break;

        default:
          this.currentTable = '';
          break;
      }
    } else {
      this.currentTable = '';
    }
  }
}
